/**
 * Checklist Findings Map
 *
 * Computes a per-checklist-item findings summary from the audit-level
 * findings array. Used by ChecklistRow for the findings indicator badge
 * and by useChecklistData for the "Has Findings" filter.
 */

import type { AuditFinding } from "@/hooks/use-audit-detail";
import type { SeverityType } from "@/packages/domains/findings";
import type { ChecklistItemId } from "@/packages/types";

export interface ChecklistFindingsSummary {
  count: number;
  worstSeverity: SeverityType;
  worstCount: number;
}

const SEVERITY_PRIORITY: Record<SeverityType, number> = {
  critical: 0,
  major: 1,
  minor: 2,
  observation: 3,
};

/**
 * Build a map of checklistItemId to findings summary from audit findings.
 * Only includes items that have at least one finding.
 */
export function buildFindingsMap(
  findings: AuditFinding[] | undefined,
): Map<ChecklistItemId, ChecklistFindingsSummary> {
  const map = new Map<ChecklistItemId, ChecklistFindingsSummary>();

  if (!findings) {
    return map;
  }

  const severityCounts = new Map<ChecklistItemId, Map<SeverityType, number>>();

  for (const finding of findings) {
    if (!finding.checklistItemId) {
      continue;
    }

    let counts = severityCounts.get(finding.checklistItemId);
    if (!counts) {
      counts = new Map<SeverityType, number>();
      severityCounts.set(finding.checklistItemId, counts);
    }

    counts.set(finding.severity, (counts.get(finding.severity) ?? 0) + 1);
  }

  for (const [itemId, counts] of severityCounts) {
    let worstSeverity: SeverityType = "observation";
    let totalCount = 0;

    for (const [severity, count] of counts) {
      totalCount += count;
      if (SEVERITY_PRIORITY[severity] < SEVERITY_PRIORITY[worstSeverity]) {
        worstSeverity = severity;
      }
    }

    map.set(itemId, {
      count: totalCount,
      worstSeverity,
      worstCount: counts.get(worstSeverity) ?? 0,
    });
  }

  return map;
}
